import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Stack,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Checkbox,
  ButtonBase,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AssignmentInd as AssignmentIndIcon,
  ExpandMore as ExpandMoreIcon,
  Place as PlaceIcon,
  Search as SearchIcon,
  ChairAlt as ChairIcon,
  Refresh as RefreshIcon,
  Archive as ArchiveIcon,
  Print as PrintIcon,
  Folder as FolderIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  getAllSofas,
  createSofa,
  updateSofa,
  deleteSofa,
  getAllSofaModels,
  getAllFabrics,
  getAllUsers,
  assignWorkers,
  archiveSofa,
  getNextSofaNumber,
  reorderSofas,
} from '../api';
import SofaFormDialog from './SofaFormDialog';
import { can } from '../capabilities';

const ITEMS_PER_GROUP = 10;

const Plan = ({ user }) => {
  const canCreate = can(user, 'createSofa');
  const canEdit = can(user, 'editSofa');
  const canDelete = can(user, 'deleteSofa');
  const canReorder = can(user, 'reorderSofas');
  const canAssign = can(user, 'assignSofaWorkers');
  const canArchive = can(user, 'archiveSofa');
  const [sofas, setSofas] = useState([]);
  const [models, setModels] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Память открытых папок (localStorage)
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('plan_expanded_groups');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('plan_expanded_groups', JSON.stringify(expandedGroups)); } catch {}
  }, [expandedGroups]);
  // Массовые операции
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openBulkAssignDialog, setOpenBulkAssignDialog] = useState(false);
  const [bulkAssignData, setBulkAssignData] = useState({ frame_worker_id: '', sewing_worker_id: '', upholstery_worker_id: '' });
  
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [currentSofa, setCurrentSofa] = useState(null);
  const [printList, setPrintList] = useState(new Set());
  
  const [formData, setFormData] = useState({
    number: '',
    assignment: '',
    destination: '',
    model_id: '',
    fabric_id: '',
    fabric_id_2: '',
    fabric_name: '',
    fabric_name_2: '',
    fabric_status: 'not_available',
    category: 'waiting',
    price_type: 'standard',
    custom_price: '',
    delivery_date: '',
    notes: '',
  });

  const [editingDateId, setEditingDateId] = useState(null);
  const [editingDateValue, setEditingDateValue] = useState('');

  // Порядок папок-направлений: храним в localStorage
  const [destinationOrder, setDestinationOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('plan_destination_order');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const persistDestinationOrder = (order) => {
    setDestinationOrder(order);
    try { localStorage.setItem('plan_destination_order', JSON.stringify(order)); } catch {}
  };

  const [assignFormData, setAssignFormData] = useState({
    frame_worker_id: '',
    sewing_worker_id: '',
    upholstery_worker_id: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sofasRes, modelsRes, fabricsRes, usersRes] = await Promise.all([
        getAllSofas({ is_archived: false }),
        getAllSofaModels(),
        getAllFabrics(),
        canAssign ? getAllUsers() : Promise.resolve({ data: [] }),
      ]);
      setSofas(sofasRes.data);
      setModels(modelsRes.data);
      setFabrics(fabricsRes.data);
      setWorkers(usersRes.data.filter(user => ['frame_worker', 'sewer', 'upholsterer'].includes(user.role)));
      setError(null);
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [canAssign]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const modelsById = useMemo(() => new Map(models.map(model => [model.id, model])), [models]);
  const fabricsById = useMemo(() => new Map(fabrics.map(fabric => [fabric.id, fabric])), [fabrics]);
  const workersById = useMemo(() => new Map(workers.map(worker => [worker.id, worker])), [workers]);

  // Функция расчёта прогресса - должна быть определена ДО использования
  const calculateProgress = useCallback((sofa) => {
    let completed = 0;
    // Считаем completed как 1, in_progress как 0.5
    if (sofa.frame_status === 'completed') completed += 1;
    else if (sofa.frame_status === 'in_progress') completed += 0.5;
    
    if (sofa.sewing_status === 'completed') completed += 1;
    else if (sofa.sewing_status === 'in_progress') completed += 0.5;
    
    if (sofa.upholstery_status === 'completed') completed += 1;
    else if (sofa.upholstery_status === 'in_progress') completed += 0.5;
    
    return (completed / 3) * 100;
  }, []);

  // Фильтрация диванов
  const filteredSofas = useMemo(() => sofas.filter(sofa => {
    // Поиск
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        sofa.number?.toLowerCase().includes(query) ||
        sofa.assignment?.toLowerCase().includes(query) ||
        sofa.destination?.toLowerCase().includes(query) ||
        modelsById.get(sofa.model_id)?.name?.toLowerCase().includes(query) ||
        fabricsById.get(sofa.fabric_id)?.name?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    
    // Статус
    if (statusFilter !== 'all') {
      const progress = calculateProgress(sofa);
      if (statusFilter === 'pending' && progress > 0) return false;
      if (statusFilter === 'in_progress' && (progress === 0 || progress === 100)) return false;
      if (statusFilter === 'completed' && progress < 100) return false;
    }
    
    return true;
  }), [sofas, searchQuery, statusFilter, modelsById, fabricsById, calculateProgress]);

  // Группировка по месту назначения
  const groupedByDestination = useMemo(() => filteredSofas.reduce((groups, sofa) => {
    const destination = sofa.destination || 'Без назначения';
    if (!groups[destination]) {
      groups[destination] = [];
    }
    groups[destination].push(sofa);
    return groups;
  }, {}), [filteredSofas]);

  // Разбиение на подгруппы по 10 штук
  const splitIntoChunks = useCallback((arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }, []);

  const handleAccordionChange = (key) => (event, isExpanded) => {
    setExpandedGroups(prev => ({ ...prev, [key]: isExpanded }));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#6b7280',
      received: '#1e40af',
      in_progress: '#475569',
      completed: '#059669',
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ожидание',
      received: 'Получено',
      in_progress: 'В работе',
      completed: 'Готово',
    };
    return labels[status] || status;
  };

  const getFabricStatusLabel = (status) => {
    const labels = {
      available: 'Есть',
      not_available: 'Нет',
      expected: 'Ожидаем',
    };
    return labels[status] || status;
  };

  const getFabricStatusColor = (status) => {
    const colors = {
      available: 'success',
      not_available: 'error',
      expected: 'warning',
    };
    return colors[status] || 'default';
  };

  const getWorkerName = (workerId) => {
    const worker = workersById.get(workerId);
    return worker ? worker.full_name : 'Не назначен';
  };

  const getProgressColor = (progress) => {
    if (progress === 100) return 'success';
    if (progress > 0) return 'warning';
    return 'inherit';
  };

  const handlePrint = (ids = printList) => {
    const selectedSofas = sofas.filter(s => ids.has(s.id));
    if (selectedSofas.length === 0) { alert('Выберите диваны для печати'); return; }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Браузер заблокировал окно печати. Разрешите всплывающие окна для этого сайта.');
      return;
    }
    const printableRows = selectedSofas.map((sofa, idx) => {
      const modelName = modelsById.get(sofa.model_id)?.name || '-';
      const fabricName = fabricsById.get(sofa.fabric_id)?.name || sofa.fabric_name || '-';
      const fabricName2 = fabricsById.get(sofa.fabric_id_2)?.name || sofa.fabric_name_2 || '-';
      const frameW = getWorkerName(sofa.frame_worker_id);
      const sewingW = getWorkerName(sofa.sewing_worker_id);
      const upholW = getWorkerName(sofa.upholstery_worker_id);
      const dateStr = sofa.delivery_date ? sofa.delivery_date.split('-').reverse().join('.') : '-';
      return [idx + 1, dateStr, sofa.number, sofa.assignment || '-', sofa.destination || '-', modelName,
        fabricName, fabricName2, frameW, sewingW, upholW, sofa.notes || '-'];
    });

    const doc = printWindow.document;
    doc.title = 'GallantCRM - Печать';
    const style = doc.createElement('style');
    style.textContent = 'body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #333;padding:6px 8px;text-align:left;font-size:12px;vertical-align:top}th{background:#1e40af;color:#fff}h2{margin:0 0 5px}p{margin:0 0 10px;color:#666}td:nth-child(2){font-size:14px;font-weight:700;white-space:nowrap}td:last-child{color:#d32f2f;font-weight:700}@media print{body{padding:10px}}';
    doc.head.appendChild(style);
    const title = doc.createElement('h2');
    title.textContent = 'GallantCRM - Лист производства';
    const subtitle = doc.createElement('p');
    subtitle.textContent = `Дата: ${new Date().toLocaleDateString('ru-RU')} · Кол-во: ${selectedSofas.length}`;
    const table = doc.createElement('table');
    const headers = ['#', 'Дата', 'Номер', 'Назначение', 'Направление', 'Модель', 'Ткань', 'Ткань 2', 'Каркас', 'Швея', 'Обивщик', 'Примечания'];
    const thead = doc.createElement('thead');
    const headerRow = doc.createElement('tr');
    headers.forEach((label) => {
      const th = doc.createElement('th');
      th.textContent = label;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    const tbody = doc.createElement('tbody');
    printableRows.forEach((values) => {
      const row = doc.createElement('tr');
      values.forEach((value) => {
        const cell = doc.createElement('td');
        cell.textContent = String(value ?? '-');
        row.appendChild(cell);
      });
      tbody.appendChild(row);
    });
    table.append(thead, tbody);
    doc.body.replaceChildren(title, subtitle, table);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Обработчики диалогов
  const handleOpenAddDialog = async () => {
    if (!canCreate) return;
    let nextNumber = '';
    try {
      const res = await getNextSofaNumber();
      nextNumber = res.data.next_number;
    } catch (e) {
      nextNumber = `${Date.now().toString().slice(-6)}`;
    }
    setFormData({
      number: nextNumber,
      assignment: '',
      destination: '',
      model_id: '',
      fabric_id: '',
      fabric_id_2: '',
      fabric_name: '',
      fabric_name_2: '',
      fabric_status: 'not_available',
      category: 'waiting',
      price_type: 'standard',
      custom_price: '',
      delivery_date: '',
      notes: '',
    });
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (sofa) => {
    setCurrentSofa(sofa);
    setFormData({
      number: sofa.number || '',
      assignment: sofa.assignment || '',
      destination: sofa.destination || '',
      model_id: sofa.model_id || '',
      fabric_id: sofa.fabric_id || '',
      fabric_id_2: sofa.fabric_id_2 || '',
      fabric_name: sofa.fabric_name || '',
      fabric_name_2: sofa.fabric_name_2 || '',
      fabric_status: sofa.fabric_status || 'not_available',
      category: sofa.category || 'waiting',
      price_type: sofa.price_type || 'standard',
      custom_price: sofa.custom_price || '',
      delivery_date: sofa.delivery_date || '',
      notes: sofa.notes || '',
    });
    setOpenEditDialog(true);
  };

  const handleOpenAssignDialog = (sofa) => {
    if (!canAssign) return;
    setCurrentSofa(sofa);
    setAssignFormData({
      frame_worker_id: sofa.frame_worker_id || '',
      sewing_worker_id: sofa.sewing_worker_id || '',
      upholstery_worker_id: sofa.upholstery_worker_id || '',
    });
    setOpenAssignDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenAssignDialog(false);
    setCurrentSofa(null);
  };

  const handleAssignFormChange = (field, value) => {
    setAssignFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateSofa = async (form) => {
    if (!canCreate) return;
    const data = form || formData;
    if (!data.number || !data.model_id) {
      alert('Пожалуйста, заполните обязательные поля: Номер и Модель');
      return;
    }

    try {
      const dataToSend = {
        number: data.number,
        model_id: parseInt(data.model_id),
        fabric_id: data.fabric_id ? parseInt(data.fabric_id) : null,
        fabric_id_2: data.fabric_id_2 ? parseInt(data.fabric_id_2) : null,
        fabric_name: data.fabric_name || null,
        fabric_name_2: data.fabric_name_2 || null,
        category: data.category || 'waiting',
        fabric_status: data.fabric_status || 'not_available',
        price_type: data.price_type || 'standard',
        assignment: data.assignment || null,
        destination: data.destination || null,
        notes: data.notes || null,
        delivery_date: data.delivery_date || null,
        custom_price: data.custom_price || null,
      };
      const created = await createSofa(dataToSend);
      // Новый заказ идёт первым, как при загрузке с сервера; ручной порядок остальных сохраняется.
      if (created?.data) {
        setSofas(prev => [created.data, ...prev]);
        // Обновляем ткани — бэкенд списал количество при создании дивана
        getAllFabrics().then(res => setFabrics(res.data)).catch(() => {});
      } else {
        // fallback если API не вернул объект
        const sy = window.scrollY;
        await loadData();
        requestAnimationFrame(() => window.scrollTo(0, sy));
      }
      handleCloseDialogs();
    } catch (error) {
      console.error('Ошибка создания дивана:', error);
      alert('Ошибка создания дивана');
    }
  };

  const handleUpdateSofa = async (form) => {
    if (!canEdit) return;
    const data = form || formData;
    try {
      const dataToSend = {
        number: data.number || undefined,
        model_id: data.model_id ? parseInt(data.model_id) : undefined,
        fabric_id: data.fabric_id ? parseInt(data.fabric_id) : null,
        fabric_id_2: data.fabric_id_2 ? parseInt(data.fabric_id_2) : null,
        fabric_name: data.fabric_name || null,
        fabric_name_2: data.fabric_name_2 || null,
        category: data.category || undefined,
        fabric_status: data.fabric_status || undefined,
        price_type: data.price_type || undefined,
        assignment: data.assignment || null,
        destination: data.destination || null,
        notes: data.notes || null,
        delivery_date: data.delivery_date || null,
        custom_price: data.custom_price ? parseFloat(data.custom_price) : null,
      };
      const sofaId = currentSofa.id;
      // Оптимистично обновляем локально — скролл не сбрасывается
      const optimisticPatch = {};
      Object.entries(dataToSend).forEach(([k, v]) => { if (v !== undefined) optimisticPatch[k] = v; });
      setSofas(prev => prev.map(s => s.id === sofaId ? { ...s, ...optimisticPatch } : s));
      handleCloseDialogs();
      try {
        await updateSofa(sofaId, dataToSend);
        // Ткани могли измениться при смене модели/ткани
        getAllFabrics().then(res => setFabrics(res.data)).catch(() => {});
      } catch (e) {
        console.error('Ошибка обновления — перезагружаю:', e);
        alert('Ошибка обновления дивана');
        await loadData();
      }
    } catch (error) {
      console.error('Ошибка обновления дивана:', error);
      alert('Ошибка обновления дивана');
    }
  };

  const handleInlineDateSave = async (sofaId, dateValue) => {
    if (!canEdit) return;
    // Оптимистично
    setSofas(prev => prev.map(s => s.id === sofaId ? { ...s, delivery_date: dateValue || null } : s));
    setEditingDateId(null);
    try {
      await updateSofa(sofaId, { delivery_date: dateValue || null });
    } catch (error) {
      console.error('Ошибка обновления даты:', error);
      await loadData();
    }
  };

  const handleAssignWorkers = async () => {
    if (!canAssign) return;
    // Оптимистичное обновление — без перезагрузки всей страницы, не сбрасываем скролл
    const sofaId = currentSofa.id;
    const newAssign = {
      frame_worker_id: assignFormData.frame_worker_id ? parseInt(assignFormData.frame_worker_id) : null,
      sewing_worker_id: assignFormData.sewing_worker_id ? parseInt(assignFormData.sewing_worker_id) : null,
      upholstery_worker_id: assignFormData.upholstery_worker_id ? parseInt(assignFormData.upholstery_worker_id) : null,
    };
    setSofas(prev => prev.map(s => s.id === sofaId ? { ...s, ...newAssign } : s));
    handleCloseDialogs();
    try {
      await assignWorkers(sofaId, {
        frame_worker_id: newAssign.frame_worker_id,
        sewer_id: newAssign.sewing_worker_id,
        upholsterer_id: newAssign.upholstery_worker_id,
      });
    } catch (error) {
      console.error('Ошибка назначения работников:', error);
      alert('Ошибка назначения работников — перезагружаю список');
      await loadData();
    }
  };

  const handleDeleteSofa = async (id) => {
    if (!canDelete) return;
    if (window.confirm('Вы уверены, что хотите удалить диван?')) {
      // Оптимистично убираем из списка
      setSofas(prev => prev.filter(s => s.id !== id));
      try {
        await deleteSofa(id);
      } catch (error) {
        console.error('Ошибка удаления дивана:', error);
        alert('Ошибка удаления');
        await loadData();
      }
    }
  };

  const handleArchiveSofa = async (sofa) => {
    if (!canArchive) return;
    if (window.confirm('Отправить диван в архив?')) {
      // Оптимистично убираем
      setSofas(prev => prev.filter(s => s.id !== sofa.id));
      try {
        await archiveSofa(sofa.id, 'Отправлен в архив вручную');
      } catch (error) {
        console.error('Ошибка архивации:', error);
        alert('Ошибка архивации дивана');
        await loadData();
      }
    }
  };

  // ============ МАССОВЫЕ ОПЕРАЦИИ ============
  const toggleSelected = (sofaId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(sofaId)) next.delete(sofaId); else next.add(sofaId);
      return next;
    });
  };
  const selectAllInList = (list, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      list.forEach(s => checked ? next.add(s.id) : next.delete(s.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const finishBulkOperation = async (ids, results, actionLabel) => {
    const succeededIds = ids.filter((_, index) => results[index].status === 'fulfilled');
    const failedIds = ids.filter((_, index) => results[index].status === 'rejected');
    setSelectedIds(new Set(failedIds));
    await loadData();
    if (failedIds.length > 0) {
      alert(`${actionLabel}: успешно ${succeededIds.length}, ошибок ${failedIds.length}. Неудавшиеся диваны оставлены выбранными.`);
    } else {
      alert(`${actionLabel}: успешно ${succeededIds.length}, ошибок 0.`);
    }
  };

  const handleBulkDelete = async () => {
    if (!canDelete) return;
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Удалить ${selectedIds.size} диван(ов) безвозвратно?`)) return;
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map(id => deleteSofa(id)));
    await finishBulkOperation(ids, results, 'Удаление');
  };
  const handleBulkArchive = async () => {
    if (!canArchive) return;
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Отправить ${selectedIds.size} диван(ов) в архив?`)) return;
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map(id => archiveSofa(id, 'Массовая архивация')));
    await finishBulkOperation(ids, results, 'Архивация');
  };
  const handleOpenBulkAssign = () => {
    if (!canAssign) return;
    setBulkAssignData({ frame_worker_id: '', sewing_worker_id: '', upholstery_worker_id: '' });
    setOpenBulkAssignDialog(true);
  };
  const handleBulkAssign = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const payload = {
      frame_worker_id: bulkAssignData.frame_worker_id ? parseInt(bulkAssignData.frame_worker_id) : null,
      sewer_id: bulkAssignData.sewing_worker_id ? parseInt(bulkAssignData.sewing_worker_id) : null,
      upholsterer_id: bulkAssignData.upholstery_worker_id ? parseInt(bulkAssignData.upholstery_worker_id) : null,
    };
    const results = await Promise.allSettled(ids.map(id => assignWorkers(id, payload)));
    const failedCount = results.filter(result => result.status === 'rejected').length;
    if (failedCount === 0) {
      setOpenBulkAssignDialog(false);
    }
    await finishBulkOperation(ids, results, 'Назначение');
  };

  // Drag-and-drop: перенос дивана внутри направления или между направлениями.
  // Ключевой принцип: порядок внутри папки берём НАПРЯМУЮ из текущего state
  // (именно этот порядок видит пользователь и именно на него ссылаются индексы
  // от react-beautiful-dnd). Никаких локальных .sort() — иначе индекс из drop
  // не соответствует визуальному положению.
  const handleSofaDragEnd = async (result) => {
    if (!canReorder) return;
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const sourceDest = source.droppableId.replace('sofas-', '');
    const destDest = destination.droppableId.replace('sofas-', '');
    const movedSofa = sofas.find((s) => String(s.id) === draggableId);
    if (!movedSofa) return;

    const normDest = (key) => (key === 'Без назначения' ? null : key);
    const folderKey = (s) => s.destination || 'Без назначения';

    // 1. Копия переносимого дивана с обновлённым destination (без мутаций).
    const updatedMoved = sourceDest !== destDest
      ? { ...movedSofa, destination: normDest(destDest) }
      : movedSofa;

    // 2. Раскладываем ТЕКУЩИЙ state по папкам в том же порядке, в каком он в state.
    //    Переносимый диван временно исключаем — вставим его отдельно.
    const byFolder = new Map();
    for (const s of sofas) {
      if (s.id === movedSofa.id) continue;
      const key = folderKey(s);
      if (!byFolder.has(key)) byFolder.set(key, []);
      byFolder.get(key).push(s);
    }
    if (!byFolder.has(destDest)) byFolder.set(destDest, []);

    // 3. Вставляем переносимый диван в целевую папку на индекс, присланный RBDnd.
    const destList = byFolder.get(destDest);
    const targetIdx = Math.min(destination.index, destList.length);
    destList.splice(targetIdx, 0, updatedMoved);

    // 4. Собираем итоговый плоский массив, сохраняя исходный порядок папок.
    const folderOrder = sortedDestinationKeys();
    const seenFolders = new Set();
    const finalSofas = [];
    let globalPos = 0;
    for (const folder of folderOrder) {
      const list = byFolder.get(folder);
      if (!list) continue;
      seenFolders.add(folder);
      for (const s of list) {
        finalSofas.push({ ...s, position: globalPos });
        globalPos++;
      }
    }
    // Новые папки (если destination указан в папку, которой ещё не было в folderOrder).
    for (const [folder, list] of byFolder) {
      if (seenFolders.has(folder)) continue;
      for (const s of list) {
        finalSofas.push({ ...s, position: globalPos });
        globalPos++;
      }
    }

    // Оптимистично показываем новое состояние сразу — без принудительного reload.
    setSofas(finalSofas);

    try {
      if (sourceDest !== destDest) {
        await updateSofa(movedSofa.id, { destination: normDest(destDest) });
      }
      const allOrderedIds = finalSofas.map((s) => s.id);
      await reorderSofas(allOrderedIds);
    } catch (error) {
      console.error('Ошибка drag-and-drop:', error);
      // Только при ошибке ресинхронизируемся с БД, чтобы не видеть призрачное состояние.
      await loadData();
    }
  };

  // Drag-and-drop направлений (папок)
  const handleDestinationDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const currentOrder = sortedDestinationKeys();
    const [moved] = currentOrder.splice(result.source.index, 1);
    currentOrder.splice(result.destination.index, 0, moved);
    persistDestinationOrder(currentOrder);
  };

  // Сортировка направлений: сначала по сохранённому порядку, остальные — по алфавиту
  const sortedDestinationKeys = () => {
    const allKeys = Object.keys(groupedByDestination);
    const ordered = destinationOrder.filter((k) => allKeys.includes(k));
    const remaining = allKeys.filter((k) => !ordered.includes(k)).sort((a, b) => a.localeCompare(b));
    return [...ordered, ...remaining];
  };

  // Рендер таблицы диванов с поддержкой drag-and-drop
  const renderSofaTable = (sofaList, destinationKey) => (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1180 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell padding="checkbox" width="3%">
              <Checkbox
                inputProps={{ 'aria-label': `Выбрать все диваны в направлении ${destinationKey}` }}
                size="small"
                checked={sofaList.length > 0 && sofaList.every(s => selectedIds.has(s.id))}
                indeterminate={sofaList.some(s => selectedIds.has(s.id)) && !sofaList.every(s => selectedIds.has(s.id))}
                onChange={(e) => selectAllInList(sofaList, e.target.checked)}
              />
            </TableCell>
            <TableCell width="4%" align="center" sx={{ px: 0 }} title="Перетащите для изменения порядка">↕</TableCell>
            <TableCell width="9%" sx={{ fontSize: '0.95rem' }}>📅 Дата</TableCell>
            <TableCell width="7%">Номер</TableCell>
            <TableCell width="10%">Назначение</TableCell>
            <TableCell width="10%">Модель</TableCell>
            <TableCell width="9%">Ткань</TableCell>
            <TableCell width="9%">Ткань 2</TableCell>
            <TableCell width="6%">Наличие</TableCell>
            <TableCell width="8%">Каркас</TableCell>
            <TableCell width="8%">Шитье</TableCell>
            <TableCell width="8%">Обивка</TableCell>
            <TableCell width="7%">Прогресс</TableCell>
            <TableCell width="10%">Действия</TableCell>
          </TableRow>
        </TableHead>
        <Droppable droppableId={`sofas-${destinationKey}`} type="SOFA">
          {(provided, snapshot) => (
            <TableBody
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={snapshot.isDraggingOver ? { bgcolor: 'action.hover' } : {}}
            >
              {sofaList.map((sofa, idx) => {
                const progress = calculateProgress(sofa);
                return (
                  <Draggable key={sofa.id} draggableId={String(sofa.id)} index={idx} isDragDisabled={!canReorder}>
                    {(dragProvided, dragSnapshot) => (
                      <TableRow
                        hover
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        sx={dragSnapshot.isDragging ? {
                          bgcolor: 'action.hover',
                          boxShadow: 4,
                          '& td': { bgcolor: 'background.paper' },
                          display: 'table',
                          tableLayout: 'fixed',
                          width: '100%',
                        } : {}}
                      >
                <TableCell padding="checkbox">
                  {(canAssign || canArchive || canDelete) && <Checkbox
                    inputProps={{ 'aria-label': `Выбрать диван ${sofa.number}` }}
                    size="small"
                    checked={selectedIds.has(sofa.id)}
                    onChange={() => toggleSelected(sofa.id)}
                  />}
                </TableCell>
                <TableCell sx={{ px: 0 }} {...dragProvided.dragHandleProps}>
                  <Box role="button" tabIndex={0} aria-label={`Перетащить диван ${sofa.number}`} display="flex" justifyContent="center" alignItems="center" sx={{ cursor: 'grab', color: 'text.secondary', touchAction: 'none', '&:hover': { color: 'primary.main' } }}>
                    <DragIcon fontSize="small" />
                  </Box>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  {canEdit && editingDateId === sofa.id ? (
                    <TextField
                      type="date"
                      size="small"
                      value={editingDateValue}
                      onChange={(e) => setEditingDateValue(e.target.value)}
                      onBlur={() => handleInlineDateSave(sofa.id, editingDateValue)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleInlineDateSave(sofa.id, editingDateValue);
                        if (e.key === 'Escape') setEditingDateId(null);
                      }}
                      autoFocus
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, '& input': { fontSize: '0.95rem', fontWeight: 700, py: 0.5 } }}
                    />
                  ) : (
                    <Tooltip title="Кликните для изменения даты">
                      <ButtonBase
                        aria-label={`Изменить дату доставки дивана ${sofa.number}`}
                        sx={{ fontSize: '1rem', fontWeight: 700, color: sofa.delivery_date ? 'primary.main' : 'text.disabled', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => { if (canEdit) { setEditingDateId(sofa.id); setEditingDateValue(sofa.delivery_date || ''); } }}
                      >
                        {sofa.delivery_date ? sofa.delivery_date.split('-').reverse().join('.') : '— нет —'}
                      </ButtonBase>
                    </Tooltip>
                  )}
                  {sofa.notes && (
                    <Tooltip title={sofa.notes} placement="top" arrow>
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.25, wordBreak: 'break-word', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                      >
                        {sofa.notes}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={sofa.number} 
                    size="small" 
                    color={sofa.price_type === 'custom' ? 'secondary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {sofa.assignment || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {modelsById.get(sofa.model_id)?.name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {fabricsById.get(sofa.fabric_id)?.name || sofa.fabric_name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {fabricsById.get(sofa.fabric_id_2)?.name || sofa.fabric_name_2 || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getFabricStatusLabel(sofa.fabric_status)}
                    color={getFabricStatusColor(sofa.fabric_status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={getStatusLabel(sofa.frame_status || 'pending')}>
                    <Chip
                      label={getWorkerName(sofa.frame_worker_id)}
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(sofa.frame_status || 'pending'), 
                        color: '#fff',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={getStatusLabel(sofa.sewing_status || 'pending')}>
                    <Chip
                      label={getWorkerName(sofa.sewing_worker_id)}
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(sofa.sewing_status || 'pending'), 
                        color: '#fff',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={getStatusLabel(sofa.upholstery_status || 'pending')}>
                    <Chip
                      label={getWorkerName(sofa.upholstery_worker_id)}
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(sofa.upholstery_status || 'pending'), 
                        color: '#fff',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progress} 
                      color={getProgressColor(progress)}
                      sx={{ width: 50, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption">{Math.round(progress)}%</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {canAssign && <Tooltip title="Назначить работников">
                      <IconButton aria-label={`Назначить работников на диван ${sofa.number}`} size="small" onClick={() => handleOpenAssignDialog(sofa)}>
                        <AssignmentIndIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>}
                    <Tooltip title={canEdit ? 'Редактировать' : 'Подробнее'}>
                      <IconButton aria-label={`Редактировать диван ${sofa.number}`} size="small" onClick={() => handleOpenEditDialog(sofa)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canArchive && progress === 100 && (
                      <Tooltip title="В архив">
                        <IconButton aria-label={`Архивировать диван ${sofa.number}`} size="small" onClick={() => handleArchiveSofa(sofa)} color="primary">
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && <Tooltip title="Удалить">
                      <IconButton aria-label={`Удалить диван ${sofa.number}`} size="small" onClick={() => handleDeleteSofa(sofa.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>}
                  </Stack>
                </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </TableBody>
          )}
        </Droppable>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
          <ChairIcon />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            План
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Производственный план диванов
          </Typography>
        </Box>
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={`Всего: ${filteredSofas.length}`} 
            color="primary" 
            variant="outlined"
          />
          {selectedIds.size > 0 && (
            <>
              <Chip
                label={`Выбрано: ${selectedIds.size}`}
                color="warning"
                onDelete={clearSelection}
                sx={{ fontWeight: 700 }}
              />
              {canAssign && <Button size="small" variant="outlined" color="primary" startIcon={<AssignmentIndIcon />} onClick={handleOpenBulkAssign}>
                Назначить
              </Button>}
              {canArchive && <Button size="small" variant="outlined" color="warning" startIcon={<ArchiveIcon />} onClick={handleBulkArchive}>
                В архив
              </Button>}
              {canDelete && <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete}>
                Удалить
              </Button>}
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={() => {
                  const ids = new Set(selectedIds);
                  setPrintList(ids);
                  handlePrint(ids);
                }}
              >
                Печать
              </Button>
            </>
          )}
          {selectedIds.size === 0 && printList.size > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={() => handlePrint()}
            >
              Печать ({printList.size})
            </Button>
          )}
          {canCreate && <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Добавить
          </Button>}
          <IconButton aria-label="Обновить производственный план" onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Фильтры */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Поиск по номеру, модели, ткани..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: 0, sm: 300 }, width: { xs: '100%', sm: 'auto' } }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Статус</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Статус"
            >
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="pending">Ожидают</MenuItem>
              <MenuItem value="in_progress">В работе</MenuItem>
              <MenuItem value="completed">Готовые</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          <Typography variant="body2" color="text.secondary">
            {Object.keys(groupedByDestination).length} направлений
          </Typography>
        </Stack>
      </Paper>

      {/* Статистика направлений */}
      <Box mb={2}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(groupedByDestination)
            .sort(([, a], [, b]) => b.length - a.length)
            .slice(0, 10)
            .map(([destination, items]) => (
              <Chip
                key={destination}
                icon={<PlaceIcon />}
                label={`${destination}: ${items.length}`}
                variant="outlined"
                size="small"
                onClick={() => {
                  setExpandedGroups(prev => {
                    const newState = { ...prev };
                    // Раскрыть все подгруппы этого направления
                    const chunks = splitIntoChunks(items, ITEMS_PER_GROUP);
                    chunks.forEach((_, i) => {
                      newState[`${destination}_${i}`] = true;
                    });
                    return newState;
                  });
                }}
              />
            ))}
        </Stack>
      </Box>

      {/* Группы по направлениям */}
      {Object.keys(groupedByDestination).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ChairIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Нет диванов в плане
          </Typography>
          {canCreate && <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            sx={{ mt: 2 }}
          >
            Добавить первый диван
          </Button>}
        </Paper>
      ) : (
        <DragDropContext onDragEnd={(result) => {
          if (result.type === 'DESTINATION') handleDestinationDragEnd(result);
          else handleSofaDragEnd(result);
        }}>
          <Droppable droppableId="destinations" type="DESTINATION">
            {(destProvided) => (
              <Box ref={destProvided.innerRef} {...destProvided.droppableProps}>
                {sortedDestinationKeys().map((destination, destIdx) => {
                  const items = groupedByDestination[destination];
                  const completedCount = items.filter(s => calculateProgress(s) === 100).length;
                  const inProgressCount = items.filter(s => {
                    const p = calculateProgress(s);
                    return p > 0 && p < 100;
                  }).length;

                  return (
                    <Draggable key={destination} draggableId={`dest-${destination}`} index={destIdx} isDragDisabled={!canReorder}>
                      {(dragProvided, dragSnapshot) => (
                        <Box
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          sx={dragSnapshot.isDragging ? { boxShadow: 6, bgcolor: 'background.paper' } : {}}
                        >
                          <Accordion
                            expanded={expandedGroups[destination] !== undefined ? expandedGroups[destination] : (items.length <= 20)}
                            onChange={handleAccordionChange(destination)}
                            sx={{ mb: 1 }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box display="flex" alignItems="center" width="100%" gap={2}>
                                <Box {...dragProvided.dragHandleProps} role="button" tabIndex={0} aria-label={`Перетащить направление ${destination}`} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.secondary', touchAction: 'none', '&:hover': { color: 'primary.main' } }} onClick={(e) => e.stopPropagation()}>
                                  <DragIcon fontSize="small" />
                                </Box>
                                <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                                  <FolderIcon fontSize="small" />
                                </Avatar>
                                <Box flex={1}>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {destination}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {items.length} {items.length === 1 ? 'диван' : items.length < 5 ? 'дивана' : 'диванов'}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                  {completedCount > 0 && (
                                    <Chip
                                      size="small"
                                      label={`Готово: ${completedCount}`}
                                      color="success"
                                      variant="outlined"
                                    />
                                  )}
                                  {inProgressCount > 0 && (
                                    <Chip
                                      size="small"
                                      label={`В работе: ${inProgressCount}`}
                                      color="warning"
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Paper variant="outlined" sx={{ m: 2 }}>
                                {renderSofaTable(items, destination)}
                              </Paper>
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {destProvided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Диалог добавления */}
      <SofaFormDialog
        open={openAddDialog}
        mode="add"
        title="Добавить диван"
        initialData={formData}
        models={models}
        fabrics={fabrics}
        sofas={sofas}
        onClose={handleCloseDialogs}
        onSubmit={handleCreateSofa}
      />

      {/* Диалог редактирования */}
      <SofaFormDialog
        open={openEditDialog}
        mode="edit"
        title="Редактировать диван"
        initialData={formData}
        models={models}
        fabrics={fabrics}
        sofas={sofas}
        onClose={handleCloseDialogs}
        onSubmit={handleUpdateSofa}
        readOnly={!canEdit}
      />

      {/* Диалог назначения работников */}
      <Dialog open={openAssignDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}>
        <DialogTitle>Назначить работников</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Каркасник</InputLabel>
              <Select
                value={assignFormData.frame_worker_id}
                onChange={(e) => handleAssignFormChange('frame_worker_id', e.target.value)}
                label="Каркасник"
              >
                <MenuItem value="">Не назначен</MenuItem>
                {workers.filter(w => w.role === 'frame_worker').map(worker => (
                  <MenuItem key={worker.id} value={worker.id}>{worker.full_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Швея</InputLabel>
              <Select
                value={assignFormData.sewing_worker_id}
                onChange={(e) => handleAssignFormChange('sewing_worker_id', e.target.value)}
                label="Швея"
              >
                <MenuItem value="">Не назначена</MenuItem>
                {workers.filter(w => w.role === 'sewer').map(worker => (
                  <MenuItem key={worker.id} value={worker.id}>{worker.full_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Обстрельщик</InputLabel>
              <Select
                value={assignFormData.upholstery_worker_id}
                onChange={(e) => handleAssignFormChange('upholstery_worker_id', e.target.value)}
                label="Обстрельщик"
              >
                <MenuItem value="">Не назначен</MenuItem>
                {workers.filter(w => w.role === 'upholsterer').map(worker => (
                  <MenuItem key={worker.id} value={worker.id}>{worker.full_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button onClick={() => setAssignFormData({ frame_worker_id: '', sewing_worker_id: '', upholstery_worker_id: '' })} color="warning">Снять всех</Button>
          <Box flex={1} />
          <Button onClick={handleCloseDialogs}>Отмена</Button>
          <Button onClick={handleAssignWorkers} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог массового назначения работников */}
      <Dialog open={openBulkAssignDialog} onClose={() => setOpenBulkAssignDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}>
        <DialogTitle>Назначить работников на {selectedIds.size} диван(ов)</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Выбранные работники будут назначены на все отмеченные диваны.
            Поля с «—» оставят текущее значение незатронутым? Нет — назначатся как «не назначен».
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Каркасник</InputLabel>
              <Select
                value={bulkAssignData.frame_worker_id}
                onChange={(e) => setBulkAssignData(p => ({ ...p, frame_worker_id: e.target.value }))}
                label="Каркасник"
              >
                <MenuItem value="">Не назначен</MenuItem>
                {workers.filter(w => w.role === 'frame_worker').map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.full_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Швея</InputLabel>
              <Select
                value={bulkAssignData.sewing_worker_id}
                onChange={(e) => setBulkAssignData(p => ({ ...p, sewing_worker_id: e.target.value }))}
                label="Швея"
              >
                <MenuItem value="">Не назначена</MenuItem>
                {workers.filter(w => w.role === 'sewer').map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.full_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Обстрельщик</InputLabel>
              <Select
                value={bulkAssignData.upholstery_worker_id}
                onChange={(e) => setBulkAssignData(p => ({ ...p, upholstery_worker_id: e.target.value }))}
                label="Обстрельщик"
              >
                <MenuItem value="">Не назначен</MenuItem>
                {workers.filter(w => w.role === 'upholsterer').map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.full_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button onClick={() => setOpenBulkAssignDialog(false)}>Отмена</Button>
          <Button onClick={handleBulkAssign} variant="contained">Назначить всем</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Plan;
